/*!
 * Compile a customized instance of Knockout. 🥊  @tko/builder@4.0.0-alpha8
 * (c) The Knockout.js Team - https://tko.io
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { addDisposeCallback, addOrRemoveItem, arrayFilter, arrayFirst, arrayForEach, arrayGetDistinctValues, arrayIndexOf, arrayMap, arrayPushAll, arrayRemoveItem, addCleaner, cleanNode, cloneNodes, compareArrays, createSymbolOrString, domData, extend, memoization, objectForEach, objectMap, options, parseHtmlFragment, parseJson, range, registerEventHandler, removeCleaner, removeDisposeCallback, removeNode, selectExtensions, setHtml, setTextContent, tasks, toggleDomNodeCssClass, triggerEvent, virtualElements } from '@tko/utils';
import { parseObjectLiteral } from '@tko/utils.parser';
import { LifeCycle } from '@tko/lifecycle';
import { dependencyDetection, extenders, isObservable, isSubscribable, isWriteableObservable, observable, observableArray, isObservableArray, peek, subscribable, toJS, toJSON, unwrap } from '@tko/observable';
import { computed, isComputed, isPureComputed, proxy, pureComputed, when } from '@tko/computed';
import { applyBindingAccessorsToNode, applyBindings, applyBindingsToDescendants, applyBindingsToNode, contextFor, dataFor, bindingEvent, BindingHandler, AsyncBindingHandler, setDomNodeChildrenFromArrayMapping } from '@tko/bind';
import { anonymousTemplate, domElement, nativeTemplateEngine, renderTemplate, setTemplateEngine, templateEngine } from '@tko/binding.template';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __values(o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

/**
 * A class to create the global knockout instance (ko).
 */
var domNodeDisposal = {
    addDisposeCallback: addDisposeCallback,
    removeDisposeCallback: removeDisposeCallback,
    removeNode: removeNode,
    addCleaner: addCleaner,
    removeCleaner: removeCleaner,
    get cleanExternalData() {
        return options.cleanExternalData;
    },
    set cleanExternalData(cleanerFn) {
        options.set('cleanExternalData', cleanerFn);
    }
};
var utils = Object.assign({
    addOrRemoveItem: addOrRemoveItem,
    arrayFilter: arrayFilter,
    arrayFirst: arrayFirst,
    arrayForEach: arrayForEach,
    arrayGetDistinctValues: arrayGetDistinctValues,
    arrayIndexOf: arrayIndexOf,
    arrayMap: arrayMap,
    arrayPushAll: arrayPushAll,
    arrayRemoveItem: arrayRemoveItem,
    cloneNodes: cloneNodes,
    compareArrays: compareArrays,
    createSymbolOrString: createSymbolOrString,
    domData: domData,
    domNodeDisposal: domNodeDisposal,
    extend: extend,
    filters: options.filters,
    objectForEach: objectForEach,
    objectMap: objectMap,
    parseHtmlFragment: parseHtmlFragment,
    parseJson: parseJson,
    parseObjectLiteral: parseObjectLiteral,
    peekObservable: peek,
    range: range,
    registerEventHandler: registerEventHandler,
    setDomNodeChildrenFromArrayMapping: setDomNodeChildrenFromArrayMapping,
    setHtml: setHtml,
    setTextContent: setTextContent,
    toggleDomNodeCssClass: toggleDomNodeCssClass,
    triggerEvent: triggerEvent,
    unwrapObservable: unwrap
});
var knockout = {
    // --- Utilities ---
    cleanNode: cleanNode,
    dependencyDetection: dependencyDetection,
    computedContext: dependencyDetection,
    filters: options.filters,
    ignoreDependencies: dependencyDetection.ignore,
    memoization: memoization,
    options: options,
    removeNode: removeNode,
    selectExtensions: selectExtensions,
    tasks: tasks,
    utils: utils,
    LifeCycle: LifeCycle,
    // -- Observable ---
    isObservable: isObservable,
    isSubscribable: isSubscribable,
    isWriteableObservable: isWriteableObservable,
    isWritableObservable: isWriteableObservable,
    observable: observable,
    observableArray: observableArray,
    isObservableArray: isObservableArray,
    peek: peek,
    subscribable: subscribable,
    unwrap: unwrap,
    toJS: toJS,
    toJSON: toJSON,
    proxy: proxy,
    // ... Computed ...
    computed: computed,
    dependentObservable: computed,
    isComputed: isComputed,
    isPureComputed: isPureComputed,
    pureComputed: pureComputed,
    when: when,
    // --- Templates ---
    nativeTemplateEngine: nativeTemplateEngine,
    renderTemplate: renderTemplate,
    setTemplateEngine: setTemplateEngine,
    templateEngine: templateEngine,
    templateSources: { domElement: domElement, anonymousTemplate: anonymousTemplate },
    // --- Binding ---
    applyBindingAccessorsToNode: applyBindingAccessorsToNode,
    applyBindings: applyBindings,
    applyBindingsToDescendants: applyBindingsToDescendants,
    applyBindingsToNode: applyBindingsToNode,
    contextFor: contextFor,
    dataFor: dataFor,
    BindingHandler: BindingHandler,
    AsyncBindingHandler: AsyncBindingHandler,
    virtualElements: virtualElements,
    domNodeDisposal: domNodeDisposal,
    bindingEvent: bindingEvent
};
var Builder = /** @class */ (function () {
    function Builder(_a) {
        var e_1, _b;
        var provider = _a.provider, bindings = _a.bindings, extenders$$1 = _a.extenders, filters = _a.filters, options$$1 = _a.options;
        Object.assign(knockout.options, options$$1, {
            filters: filters,
            bindingProviderInstance: provider
        });
        provider.setGlobals(knockout.options.bindingGlobals);
        if (Array.isArray(bindings)) {
            try {
                for (var bindings_1 = __values(bindings), bindings_1_1 = bindings_1.next(); !bindings_1_1.done; bindings_1_1 = bindings_1.next()) {
                    var bindingsObject = bindings_1_1.value;
                    provider.bindingHandlers.set(bindingsObject);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (bindings_1_1 && !bindings_1_1.done && (_b = bindings_1["return"])) _b.call(bindings_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        else {
            provider.bindingHandlers.set(bindings);
        }
        this.providedProperties = {
            extenders: Object.assign(extenders, extenders$$1),
            bindingHandlers: provider.bindingHandlers,
            bindingProvider: provider
        };
    }
    /**
     * @return {Object} An instance of Knockout.
     */
    Builder.prototype.create = function () {
        var additionalProperties = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            additionalProperties[_i] = arguments[_i];
        }
        var instance = Object.assign.apply(Object, __spread([{
                get getBindingHandler() { return options.getBindingHandler; },
                set getBindingHandler(fn) { options.set('getBindingHandler', fn); }
            },
            knockout,
            this.providedProperties], additionalProperties));
        instance.options.knockoutInstance = instance;
        return instance;
    };
    return Builder;
}());

export { Builder };
//# sourceMappingURL=builder.js.map
